import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { validateField, formatApiError } from '@/lib/validators';
import { Plus, RefreshCw, UserCheck, UserX, Copy, Pencil, Building2, Check, Search } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import PhoneVendorsList from './Vendors/PhoneVendorsList';
import PhoneVendorForm from './Vendors/PhoneVendorForm';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Vendors as a list, with the one column that makes the page useful.
 *
 * A three-across card grid showed nine vendors and no way to compare them. What
 * a dispatcher actually wants to know is who is already holding work — so
 * "Active items" is now a sortable column rather than a line of small print at
 * the bottom of a card.
 *
 * Add and Edit were two dialogs with identical bodies; they are one side pane.
 * The credentials reveal stays a modal on purpose: it is a one-time secret that
 * cannot be recovered, and it should stop you until you have written it down.
 */

const VALIDATORS = {
  full_name: [{ name: 'required', options: { message: 'Vendor name is required' } }],
  contact_number: [{ name: 'mobile', options: { allowEmpty: true } }],
  email: [{ name: 'email' }],
};

const EMPTY = { full_name: '', contact_number: '', email: '', address: '', linked_stages: [], remarks: '' };

/**
 * Which stages this vendor can take work at. Shared by add and edit.
 *
 * The badge says who already covers the stage, not whether the stage *could* be
 * sent out. It used to read "External" off `supports_external_vendor`, a
 * capability flag every outsourceable process carries from birth — so Binding
 * and Corrugation Pasting were labelled external on a system with no vendors at
 * all, which is the opposite of what the label was read as.
 */
function StagePicker({ stages, picked, onToggle }) {
  return (
    <div className="mt-0.5 max-h-44 space-y-0.5 overflow-y-auto rounded border border-slate-200 bg-white p-1">
      {stages.length === 0 && <p className="p-1 text-xs text-slate-400">Loading stages…</p>}
      {stages.map(stage => {
        const on = picked.includes(stage.stage_name);
        return (
          <button
            key={stage.stage_key}
            type="button"
            onClick={() => onToggle(stage.stage_name)}
            aria-pressed={on}
            className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-slate-50 ${on ? 'bg-blue-50' : ''}`}
          >
            <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
              on ? 'border-brand-blue bg-brand-blue' : 'border-slate-300'
            }`}>
              {on && <Check className="h-2.5 w-2.5 text-white" />}
            </span>
            <span className="flex-1 truncate text-[12px] text-slate-700">{stage.stage_name}</span>
            {stage.vendor_count > 0 && (
              <Badge variant="outline" title={(stage.vendor_names || []).join('\n')}
                className="shrink-0 border-slate-200 bg-slate-50 px-1 py-0 text-[9px] text-slate-500">
                {stage.vendor_count} vendor{stage.vendor_count === 1 ? '' : 's'}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function VendorManagementPage() {
  const isMobile = useIsMobile();
  const [vendors, setVendors] = useState([]);
  const [availableStages, setAvailableStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  const [pane, setPane] = useState(null); // 'add' | vendor
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/vendors/manage');
      setVendors(res.data.vendors || []);
    } catch { toast.error('Failed to load vendors'); }
    setLoading(false);
  }, []);

  const fetchStages = useCallback(async () => {
    try {
      const res = await api.get('/vendors/available-stages');
      setAvailableStages(res.data.stages || []);
    } catch { /* the picker shows its own loading note */ }
  }, []);

  useEffect(() => { fetchVendors(); fetchStages(); }, [fetchVendors, fetchStages]);

  const openAdd = () => { setForm(EMPTY); setPane('add'); };
  const openEdit = (v) => {
    setForm({
      full_name: v.full_name || '',
      contact_number: v.mobile_number || '',
      email: v.email || '',
      address: v.address || '',
      linked_stages: v.linked_stages || [],
      remarks: v.remarks || '',
    });
    setPane(v);
  };

  const save = async () => {
    const nameError = validateField('full_name', form.full_name, VALIDATORS.full_name);
    if (nameError) { toast.error(nameError); return; }
    const contactError = validateField('contact_number', form.contact_number, VALIDATORS.contact_number);
    if (contactError) { toast.error(contactError); return; }
    const emailError = validateField('email', form.email, VALIDATORS.email);
    if (emailError) { toast.error(emailError); return; }

    setSubmitting(true);
    try {
      if (pane === 'add') {
        const res = await api.post('/vendors/create', form);
        setCredentials(res.data.credentials);
        toast.success(res.data.message);
      } else {
        await api.put(`/vendors/${pane.id}`, {
          full_name: form.full_name || undefined,
          contact_number: form.contact_number || undefined,
          email: form.email || undefined,
          address: form.address || undefined,
          linked_stages: form.linked_stages.length > 0 ? form.linked_stages : undefined,
          remarks: form.remarks || undefined,
        });
        toast.success('Vendor updated');
      }
      setPane(null);
      setForm(EMPTY);
      fetchVendors();
    } catch (e) { toast.error(formatApiError(e, 'Error saving vendor')); }
    setSubmitting(false);
  };

  const toggleStatus = async (vendor) => {
    try {
      const res = await api.patch(`/vendors/${vendor.id}/toggle`);
      toast.success(res.data.message);
      fetchVendors();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleStage = (name) => setForm(f => ({
    ...f,
    linked_stages: f.linked_stages.includes(name)
      ? f.linked_stages.filter(s => s !== name)
      : [...f.linked_stages, name],
  }));

  const visible = useMemo(() => vendors.filter(v => {
    if (statusFilter && v.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return v.full_name?.toLowerCase().includes(q)
      || v.username?.toLowerCase().includes(q)
      || v.mobile_number?.toLowerCase().includes(q)
      || v.linked_stages?.some(s => s.toLowerCase().includes(q));
  }), [vendors, search, statusFilter]);

  const columns = useMemo(() => ([
    {
      key: 'full_name',
      header: 'Vendor',
      width: 'minmax(12rem,1.3fr)',
      sortable: true,
      render: (v) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{v.full_name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{v.username}</span>
        </>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      width: 'minmax(10rem,1fr)',
      render: (v) => (
        <>
          <span className="block truncate text-[12px] text-slate-600">{v.mobile_number || '—'}</span>
          {v.email && <span className="block truncate text-[11px] leading-tight text-slate-400">{v.email}</span>}
        </>
      ),
    },
    {
      key: 'linked_stages',
      header: 'Takes work at',
      width: 'minmax(12rem,1.4fr)',
      render: (v) => v.linked_stages?.length ? (
        <span className="flex flex-wrap gap-1">
          {v.linked_stages.slice(0, 3).map(s => (
            <span key={s} className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">{s}</span>
          ))}
          {v.linked_stages.length > 3 && (
            <span className="text-[10px] text-slate-400">+{v.linked_stages.length - 3}</span>
          )}
        </span>
      ) : <span className="text-slate-400">—</span>,
    },
    {
      key: 'active_orders_count',
      header: 'Active items',
      align: 'right',
      width: '7rem',
      sortable: true,
      render: (v) => (
        <span className={`font-semibold ${v.active_orders_count ? 'text-brand-blue' : 'text-slate-400'}`}>
          {v.active_orders_count || 0}
        </span>
      ),
      total: (rows) => rows.reduce((s, v) => s + (v.active_orders_count || 0), 0),
    },
    {
      key: 'status',
      header: 'Status',
      width: '6rem',
      render: (v) => (
        <span className={`text-[12px] font-medium ${v.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>
          {v.status}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '4.5rem',
      align: 'right',
      render: (v) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Pencil} label="Edit vendor" iconOnly
            onClick={() => openEdit(v)} testId={`edit-vendor-${v.id}`} />
          <CellButton
            icon={v.status === 'active' ? UserX : UserCheck}
            label={v.status === 'active' ? 'Deactivate vendor' : 'Activate vendor'}
            iconOnly
            tone={v.status === 'active' ? 'danger' : 'good'}
            onClick={() => toggleStatus(v)}
            testId={`toggle-vendor-${v.id}`}
          />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), []);

  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => {
    if (!sort?.key) return visible;
    return [...visible].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [visible, sort]);

  const activeCount = vendors.filter(v => v.status === 'active').length;
  const busyCount = vendors.filter(v => (v.active_orders_count || 0) > 0).length;
  const closeForm = () => { setPane(null); setForm(EMPTY); };

  // a one-time secret. this one really should stop you
  const credentialsDialog = (
    <Dialog open={!!credentials} onOpenChange={() => setCredentials(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Vendor credentials</DialogTitle>
          <DialogDescription>Share these with the vendor for mobile app login.</DialogDescription>
        </DialogHeader>
        {credentials && (
          <div className="space-y-2">
            {[['Username', credentials.username], ['Password', credentials.password]].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-3">
                <span className="min-w-0">
                  <span className="block text-xs text-slate-500">{label}</span>
                  <span className="block truncate font-mono font-bold text-slate-800">{value}</span>
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => copy(value)}
                  aria-label={`Copy ${label.toLowerCase()}`}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <p className="text-xs font-medium text-red-500">
              Save these now — the password cannot be recovered later.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button onClick={() => setCredentials(null)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // one card per vendor below lg; the form takes the whole screen while it is
  // open, and the one-time credentials stop you the same way either way
  if (isMobile) {
    if (pane) {
      return (
        <PhoneVendorForm
          mode={pane} form={form} onChange={setForm} availableStages={availableStages}
          submitting={submitting} onSave={save} onCancel={closeForm}
        />
      );
    }
    return (
      <>
        <PhoneVendorsList
          vendors={sorted} total={vendors.length} activeCount={activeCount} loading={loading}
          onSearch={setSearch}
          statusFilter={statusFilter} onStatusFilter={setStatusFilter}
          onNew={openAdd} onEdit={openEdit} onToggle={toggleStatus}
        />
        {credentialsDialog}
      </>
    );
  }

  return (
    <Screen data-testid="vendor-management-page">
      <CommandBar
        title="Vendors"
        subtitle="External vendors, binders and city office partners"
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchVendors, disabled: loading },
          { key: 'add', label: 'Add Vendor', icon: Plus, tone: 'accent', onClick: openAdd, testId: 'add-vendor-btn' },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Name, username, stage…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'vendors', value: vendors.length, tone: 'blue' },
          { key: 'active', label: 'Active', value: activeCount, tone: 'good' },
          { key: 'inactive', label: 'Inactive', value: vendors.length - activeCount },
          { key: null, label: 'holding work', value: busyCount, tone: busyCount ? 'warn' : 'default' },
        ]}
        activeKey={statusFilter}
        onSelect={setStatusFilter}
      />

      <Pane
        title="Vendors"
        aside={`${sorted.length} shown`}
        scroll={false}
        bodyClassName="flex flex-col"
      >
        <DataGrid
          data-testid="vendors-table"
          columns={columns}
          rows={sorted}
          loading={loading}
          getRowId={(v) => v.id}
          sort={sort}
          onSortChange={setSort}
          showTotals={sorted.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Building2 className="h-8 w-8 opacity-50" />
              No vendors yet — add your first one.
            </span>
          }
        />
      </Pane>

      <SidePane
        open={!!pane}
        onClose={closeForm}
        title={pane === 'add' ? 'Add vendor' : `Edit ${pane?.full_name || 'vendor'}`}
        subtitle={pane === 'add' ? 'Login credentials are generated on save' : pane?.username}
        width="26rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={closeForm}>Cancel</Button>
            <Button size="sm" disabled={submitting} onClick={save} data-testid="submit-vendor-btn"
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {submitting ? 'Saving…' : pane === 'add' ? 'Create vendor' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          {[
            ['Name *', 'full_name', 'Vendor or firm name', 'vendor-name-input'],
            ['Contact', 'contact_number', 'Phone number'],
            ['Email', 'email', 'Optional'],
            ['Address', 'address', 'Where the work goes'],
          ].map(([label, key, placeholder, testId]) => (
            <label key={key} className="block">
              <span className="text-[11px] font-medium text-slate-600">{label}</span>
              <Input
                className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"
                value={form[key]}
                placeholder={placeholder}
                data-testid={testId}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              />
            </label>
          ))}

          <div>
            <span className="text-[11px] font-medium text-slate-600">
              Takes work at
              {form.linked_stages.length > 0 && (
                <span className="ml-1 text-slate-400">({form.linked_stages.length} selected)</span>
              )}
            </span>
            <StagePicker stages={availableStages} picked={form.linked_stages} onToggle={toggleStage} />
          </div>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Remarks</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.remarks} placeholder="Notes…"
              onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
          </label>
        </div>
      </SidePane>

      {credentialsDialog}
    </Screen>
  );
}
